/**
 * Pure synchronous core for PDF Compare.
 *
 * No DOM, no PDF.js, no Worker globals — only data in / data out. The worker
 * loads PDFs via pdfjs-dist and feeds the text items into these functions.
 * Keeps the unit tests trivial.
 */

import DiffMatchPatch from 'diff-match-patch';
import pixelmatch from 'pixelmatch';

// Stateless across calls — diff-match-patch's instance methods don't retain
// any input data between invocations.
const dmp = new DiffMatchPatch();

/**
 * PDF.js text items have a 6-element `transform` matrix: [a, b, c, d, e, f]
 * where e is the x-offset and f is the y-offset. We use those to determine
 * position. PDF coordinates: y increases UPWARD, so the top of the page has
 * the HIGHEST y value.
 */
const Y_LINE_TOLERANCE = 4; // items within 4pt of each other are on the same line

export function sortReadingOrder(items) {
  if (!items || items.length === 0) return [];
  // Stable sort: round y to nearest tolerance bucket, then by x ascending.
  // Descending y = top-to-bottom reading order.
  return [...items].sort((a, b) => {
    const yA = Math.round(a.transform[5] / Y_LINE_TOLERANCE);
    const yB = Math.round(b.transform[5] / Y_LINE_TOLERANCE);
    if (yA !== yB) return yB - yA;
    return a.transform[4] - b.transform[4];
  });
}

export function extractTextString(items) {
  if (!items || items.length === 0) return '';
  const sorted = sortReadingOrder(items);
  let out = '';
  let prevY = null;
  for (const item of sorted) {
    const y = Math.round(item.transform[5] / Y_LINE_TOLERANCE);
    if (prevY !== null && y !== prevY) {
      out += '\n';
    } else if (out.length > 0) {
      out += ' ';
    }
    out += item.str;
    prevY = y;
  }
  return out;
}

/**
 * Word-level diff between two strings.
 *
 * Returns an array of `{type, text}` where type is 'added' | 'removed'.
 * We deliberately don't emit 'modified' here — diff-match-patch reports edits
 * as a removed-then-added pair, and downstream code can pair them up if it
 * wants. Keeping the schema flat makes diffing easier to reason about.
 *
 * Whitespace-only diffs are filtered out — they're never useful to a user.
 */
export function diffWords(textA, textB) {
  if (!textA && !textB) return [];

  // Pre-tokenize on word boundaries so diff-match-patch operates on words.
  // The trick: convert words to chars first (each unique word → unique char),
  // run char-level diff (fast), then expand back. dmp_lineMode handles this
  // for lines; we do the same for words.
  const { chars1, chars2, wordArray } = wordsToChars(textA, textB);
  const diffs = dmp.diff_main(chars1, chars2, false);
  dmp.diff_cleanupSemantic(diffs);

  const out = [];
  for (const [op, str] of diffs) {
    if (op === 0) continue; // equal, skip
    // Re-expand chars to words
    const text = [...str].map((ch) => wordArray[ch.charCodeAt(0)]).join('');
    if (!text.trim()) continue; // whitespace-only, skip
    out.push({
      type: op === 1 ? 'added' : 'removed',
      text,
    });
  }
  return out;
}

function wordsToChars(textA, textB) {
  const wordArray = ['']; // index 0 is reserved
  const wordHash = new Map();
  const encode = (text) => {
    // Split on word boundaries — but keep the separators so we can rebuild
    // the original text faithfully. Each word and each separator becomes one
    // "char" in the diff input.
    const tokens = text.split(/(\s+)/);
    let chars = '';
    for (const tok of tokens) {
      if (!tok) continue;
      let idx = wordHash.get(tok);
      if (idx === undefined) {
        wordArray.push(tok);
        idx = wordArray.length - 1;
        wordHash.set(tok, idx);
      }
      chars += String.fromCharCode(idx);
    }
    return chars;
  };
  return { chars1: encode(textA), chars2: encode(textB), wordArray };
}

/**
 * Best-effort column-layout detection. Returns the estimated column count.
 * Used downstream to surface a subtle hint when ordering may not match
 * reading order (multi-column docs confuse simple y-then-x sorting).
 *
 * Heuristic: split text by x position into clusters; if total text spans
 * >60% of page width AND there are 2+ x-clusters with a clear gap, report
 * the cluster count.
 */
export function detectColumns(items, pageWidth) {
  if (!items || items.length < 2 || !pageWidth) return 1;

  const xs = items.map((i) => i.transform[4]).sort((a, b) => a - b);
  const span = xs[xs.length - 1] - xs[0];
  // Threshold tuned so that narrow content (e.g. spanning <30% of page width)
  // doesn't get flagged as multi-column, but a typical two-column layout with
  // left margin ~50pt and right column starting ~400pt on a US Letter page
  // (span ~350pt = ~57% of 612pt) does.
  if (span < pageWidth * 0.5) return 1;

  // Bucket x positions into 20pt-wide bins, then look for gaps >50pt
  // between consecutive populated bins. Number of populated cluster
  // groups = column count.
  const bins = new Map();
  for (const x of xs) {
    const bin = Math.floor(x / 20) * 20;
    bins.set(bin, (bins.get(bin) || 0) + 1);
  }
  const sortedBins = [...bins.keys()].sort((a, b) => a - b);
  let clusterCount = 1;
  for (let i = 1; i < sortedBins.length; i++) {
    if (sortedBins[i] - sortedBins[i - 1] > 50) clusterCount++;
  }
  return clusterCount;
}

/**
 * Run a complete diff for a single page pair.
 *
 * @param {Array} items1 — text items from file1's page (or [] if page missing)
 * @param {Array} items2 — text items from file2's page (or [] if page missing)
 * @param {number} pageIndex — 0-indexed page number to stamp on each change
 * @param {object} options
 * @param {number} options.pageWidth — used for column detection, defaults to 612
 * @returns {{changes: Change[], columnCount: number}}
 */
export function diffPageTextItems(items1, items2, pageIndex, options = {}) {
  const pageWidth = options.pageWidth ?? 612;
  const textA = extractTextString(items1 || []);
  const textB = extractTextString(items2 || []);
  const rawChanges = diffWords(textA, textB);
  const columnCount = Math.max(
    detectColumns(items1 || [], pageWidth),
    detectColumns(items2 || [], pageWidth),
  );

  const changes = rawChanges.map((c, idx) => ({
    id: `p${pageIndex}-${c.type}-${idx}-${hashShort(c.text)}`,
    page: pageIndex,
    side: c.type === 'added' ? 'right' : 'left',
    type: c.type,
    text: c.text,
  }));

  const hasText = (items1?.length || 0) + (items2?.length || 0) > 0;
  return { changes, columnCount, hasText };
}

function hashShort(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h) + str.charCodeAt(i);
    h |= 0;
  }
  return (h >>> 0).toString(36).slice(0, 6);
}

/**
 * Compare two equal-sized RGBA pixel buffers. Returns an array of bounding
 * boxes (in image-pixel coordinates) covering regions of difference.
 *
 * pixelmatch returns a per-pixel mask; we flood-fill it into connected
 * components, then take the bbox of each component. Components smaller than
 * MIN_REGION pixels are ignored as anti-aliasing/noise.
 */
const PIXELMATCH_THRESHOLD = 0.1; // 0 = exact match, 1 = total mismatch
const MIN_REGION = 16;            // pixels — anything smaller is noise

export function computeVisualBboxes(rgbaA, rgbaB, width, height) {
  if (!width || !height) return [];
  const mask = new Uint8ClampedArray(width * height * 4);
  const numDiffPixels = pixelmatch(rgbaA, rgbaB, mask, width, height, {
    threshold: PIXELMATCH_THRESHOLD,
    includeAA: false,
  });
  if (numDiffPixels === 0) return [];

  // Flood-fill the mask into connected components.
  // pixelmatch writes diff pixels at full alpha (255). Non-diff pixels
  // remain at the buffer's initial alpha of 0. Reading the alpha channel
  // is invariant to the diffColor option.
  const isDiff = new Uint8Array(width * height);
  for (let i = 0; i < width * height; i++) {
    if (mask[i * 4 + 3] > 0) isDiff[i] = 1;
  }
  return floodFillBboxes(isDiff, width, height, MIN_REGION);
}

function floodFillBboxes(mask, width, height, minRegion) {
  const visited = new Uint8Array(width * height);
  const boxes = [];
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      if (!mask[i] || visited[i]) continue;
      // BFS to find the connected component.
      let minX = x, maxX = x, minY = y, maxY = y, count = 0;
      const queue = [i];
      let head = 0;
      visited[i] = 1;
      while (head < queue.length) {
        const cur = queue[head++];
        const cx = cur % width;
        const cy = Math.floor(cur / width);
        if (cx < minX) minX = cx;
        if (cx > maxX) maxX = cx;
        if (cy < minY) minY = cy;
        if (cy > maxY) maxY = cy;
        count++;
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          const nx = cx + dx;
          const ny = cy + dy;
          if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
          const ni = ny * width + nx;
          if (!mask[ni] || visited[ni]) continue;
          visited[ni] = 1;
          queue.push(ni);
        }
      }
      if (count >= minRegion) {
        boxes.push({ x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 });
      }
    }
  }
  return boxes;
}
